import { useState } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import api from '@/lib/api'
import { toast } from 'sonner'

export default function VerifyEmail() {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const emailParam = params.get('email') || ''
  // Where the user was headed before they had to register (e.g. an invitation
  // page). Forwarded on to /login so the chain Register → VerifyEmail → Login
  // survives this waiting screen and Login's post-login redirect lands them there.
  const next = params.get('next') || ''
  const [email, setEmail] = useState(emailParam)
  const [sending, setSending] = useState(false)

  // Built with URLSearchParams so an empty part can't produce `/login&next=…`.
  const loginQuery = (() => {
    const qs = new URLSearchParams()
    if (email) qs.set('email', email)
    if (next) qs.set('next', next)
    const s = qs.toString()
    return s ? `?${s}` : ''
  })()

  const resend = async () => {
    if (!email) return
    setSending(true)
    try {
      const { data } = await api.post('/auth/resend-verification', { email })
      if (data.email_sent === false) {
        toast.error(data.message || 'Unable to resend verification')
      } else {
        toast.success('Verification email sent')
      }
    } catch (error: unknown) {
      const response = (error as { response?: { data?: { error?: string } } }).response
      toast.error(response?.data?.error || 'Unable to resend verification')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Verify your email</CardTitle>
          <CardDescription>
            We sent a verification link to your email. Please click the link to activate your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-muted-foreground">Email</div>
          <div className="font-mono text-sm break-all">{email || '—'}</div>
        </CardContent>
        <CardFooter className="flex flex-col gap-3">
          <Button
            className="w-full"
            onClick={() => {
              window.location.href = `/login${loginQuery}`
            }}
          >
            I've verified — sign in
          </Button>
          <Button onClick={resend} disabled={!email || sending} variant="outline" className="w-full">
            {sending ? 'Sending…' : 'Resend verification email'}
          </Button>
          <div className="text-sm text-center text-muted-foreground">
            Already verified?{' '}
            <Link to={`/login${loginQuery}`} className="text-brand-gold hover:underline">
              Sign in
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  )
}

export const Component = VerifyEmail
